import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useSearchNotes } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { errorReporter, isFirebaseOfflineError } from '@/services/errorReportingService';
import useAlertStore from '@/stores/alertStore';
import { logger } from '@/services/loggerService';
import {
  GenesisEligibility,
  GenesisPhase,
  GENESIS_FIRESTORE_FIELD,
  PLUGIN_SESSION_KEY,
  GENESIS_LOCAL_STORAGE_KEY,
  GENESIS_FORCE_PARAM,
  GENESIS_FORCE_VALUE,
} from './types';

/**
 * Custom hook to manage the Genesis Event celebration.
 *
 * Determines eligibility based on:
 * 1. User came from the plugin (sessionStorage flag)
 * 2. User has exactly 1 note in their account
 * 3. User owns the note being viewed
 * 4. User hasn't seen the Genesis Event before (Firestore flag)
 *
 * OR: Force trigger via URL param ?celebrate=firstvideo
 *
 * @param noteId - The ID of the note being viewed
 * @param noteCreatorUserId - The creator user ID of the note
 * @returns Genesis Event state and controls
 */
export const useGenesisEvent = (noteId?: string, noteCreatorUserId?: string) => {
  const { userToken, userData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const userId = userData?.id;

  // Check for force trigger via URL parameter - computed once on mount
  // Using useMemo ensures this is calculated before any effects run
  const isForceMode = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const celebrateParam = urlParams.get(GENESIS_FORCE_PARAM);
    return celebrateParam === GENESIS_FORCE_VALUE;
  }, []);

  // Track if force mode was already used to prevent infinite re-triggers
  const forceModeUsedRef = useRef(false);

  // Eligibility state - start as eligible if force mode is active
  const [eligibility, setEligibility] = useState<GenesisEligibility>(() => {
    if (isForceMode) {
      return {
        eligible: true,
        loading: false,
        reason: 'Force triggered via URL param',
      };
    }
    return {
      eligible: false,
      loading: true,
      reason: 'Checking eligibility...',
    };
  });

  // Event phase
  const [phase, setPhase] = useState<GenesisPhase>(GenesisPhase.IDLE);

  // Firebase completion status
  const [hasCompletedBefore, setHasCompletedBefore] = useState<boolean | null>(null);
  const hasCompletedRef = useRef<boolean | null>(null);

  // Track if we've already started the event to prevent re-triggers
  const eventStartedRef = useRef(false);

  // Query user's total note count
  const { data: notesData, isLoading: isLoadingNotes } = useSearchNotes(
    {
      headers: { authorization: `Bearer ${userToken}` },
      queryParams: {
        limit: 2, // We only need to know if there's exactly 1 note
      },
    },
    {
      enabled: !!userToken && !!userId,
    },
  );

  // Listen to Firebase for completion status
  useEffect(() => {
    if (!userId) {
      setHasCompletedBefore(null);
      hasCompletedRef.current = null;
      return undefined;
    }

    const docRef = doc(collection(db, 'users'), userId);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const completed = data?.[GENESIS_FIRESTORE_FIELD] === true;
          setHasCompletedBefore(completed);
          hasCompletedRef.current = completed;
        } else {
          setHasCompletedBefore(false);
          hasCompletedRef.current = false;
        }
      },
      (error) => {
        if (isFirebaseOfflineError(error)) {
          errorReporter.expected('[Genesis] Completion status snapshot failed (client offline)', error, {
            feature: 'firebase',
            operation: 'loadCompletionStatus',
          });
        } else {
          errorReporter.critical('[Genesis] Failed to load completion status', error, {
            feature: 'firebase',
            operation: 'loadCompletionStatus',
            userId,
          });
        }
        // On error, check localStorage as fallback
        try {
          const localCompleted = localStorage.getItem(GENESIS_LOCAL_STORAGE_KEY) === 'true';
          setHasCompletedBefore(localCompleted);
          hasCompletedRef.current = localCompleted;
        } catch (localStorageError) {
          errorReporter.expected('[Genesis] localStorage fallback failed', localStorageError, {
            feature: 'firebase',
            operation: 'localStorageFallback',
          });
          // Show user a notice that progress won't be saved
          showAlert('Celebration progress cannot be saved in private browsing mode', 'info');
          setHasCompletedBefore(false);
          hasCompletedRef.current = false;
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, [userId]);

  // Determine eligibility
  useEffect(() => {
    logger.debug('genesis', 'Checking eligibility', {
      isForceMode,
      isLoadingNotes,
      hasCompletedBefore,
      userId,
      userToken: !!userToken,
      noteId,
      noteCreatorUserId,
      totalNotes: notesData?.notes?.length ?? 0,
    });

    // Skip eligibility checks in force mode - eligibility is already set
    if (isForceMode) {
      logger.debug('genesis', 'Force mode active - skipping checks');
      return;
    }

    // Still loading data
    if (isLoadingNotes || hasCompletedBefore === null) {
      logger.debug('genesis', '❌ Still loading data');
      setEligibility({
        eligible: false,
        loading: true,
        reason: 'Loading note count and completion status...',
      });
      return;
    }

    // No user logged in
    if (!userId || !userToken) {
      logger.debug('genesis', '❌ User not logged in');
      setEligibility({
        eligible: false,
        loading: false,
        reason: 'User not logged in',
      });
      return;
    }

    // No note data yet
    if (!noteId || !noteCreatorUserId) {
      logger.debug('genesis', '❌ No note data');
      setEligibility({
        eligible: false,
        loading: false,
        reason: 'Note data not available',
      });
      return;
    }

    // User doesn't own this note
    if (userId !== noteCreatorUserId) {
      logger.debug('genesis', '❌ User does not own note', { userId, noteCreatorUserId });
      setEligibility({
        eligible: false,
        loading: false,
        reason: 'User does not own this note',
      });
      return;
    }

    // Already completed the Genesis Event
    if (hasCompletedBefore) {
      logger.debug('genesis', '❌ Already completed before');
      setEligibility({
        eligible: false,
        loading: false,
        reason: 'Genesis Event already completed',
      });
      return;
    }

    // Check if user came from plugin (TEMPORARILY DISABLED FOR TESTING)
    let cameFromPlugin = false;
    try {
      cameFromPlugin = sessionStorage.getItem(PLUGIN_SESSION_KEY) === 'true';
      logger.debug('genesis', 'Plugin check', {
        sessionStorageKey: PLUGIN_SESSION_KEY,
        sessionStorageValue: sessionStorage.getItem(PLUGIN_SESSION_KEY),
        cameFromPlugin,
      });
    } catch (err) {
      logger.debug('genesis', 'SessionStorage error', { error: err });
      // sessionStorage may be unavailable in some contexts
    }
    // TEMPORARILY BYPASSING PLUGIN CHECK FOR TESTING
    logger.debug('genesis', '⚠️ SKIPPING plugin check (disabled for testing)');
    // if (!cameFromPlugin) {
    //   logger.debug('genesis', '❌ Did not come from plugin');
    //   setEligibility({
    //     eligible: false,
    //     loading: false,
    //     reason: 'User did not come from plugin',
    //   });
    //   return;
    // }

    // Check note count - must be exactly 1
    const totalNotes = notesData?.notes?.length ?? 0;
    if (totalNotes !== 1) {
      logger.debug('genesis', '❌ Wrong note count', { totalNotes });
      setEligibility({
        eligible: false,
        loading: false,
        reason: `User has ${totalNotes} notes (expected exactly 1)`,
      });
      return;
    }

    // All conditions met!
    logger.debug('genesis', '✅✅✅ ALL CONDITIONS MET - CELEBRATION SHOULD START!');
    setEligibility({
      eligible: true,
      loading: false,
    });
  }, [userId, userToken, noteId, noteCreatorUserId, hasCompletedBefore, isLoadingNotes, notesData, isForceMode]);

  /**
   * Start the Genesis Event celebration
   */
  const startEvent = useCallback(() => {
    if (eventStartedRef.current || !eligibility.eligible) {
      return;
    }

    // In force mode, mark as used to prevent re-triggers within same session
    if (isForceMode) {
      forceModeUsedRef.current = true;
    }

    eventStartedRef.current = true;
    setPhase(GenesisPhase.CELEBRATION);

    // Clear the plugin flag so it doesn't trigger again (skip in force mode)
    if (!isForceMode) {
      try {
        sessionStorage.removeItem(PLUGIN_SESSION_KEY);
      } catch {
        // sessionStorage may be unavailable
      }
    }
  }, [eligibility.eligible, isForceMode]);

  /**
   * Mark the event as completed in Firebase
   * In force mode, skip persistence so the event can be re-triggered
   */
  const markCompleted = useCallback(async (): Promise<boolean> => {
    // In force mode, skip persistence to allow re-triggering
    if (isForceMode) {
      return true;
    }

    if (!userId) {
      return false;
    }

    // Mark in localStorage first (fast fallback)
    try {
      localStorage.setItem(GENESIS_LOCAL_STORAGE_KEY, 'true');
    } catch {
      // localStorage may be unavailable
    }

    try {
      const docRef = doc(collection(db, 'users'), userId);
      await setDoc(
        docRef,
        {
          [GENESIS_FIRESTORE_FIELD]: true,
          genesisEventCompletedAt: new Date().toISOString(),
          firstNoteId: noteId,
        },
        { merge: true },
      );
      return true;
    } catch (error) {
      if (isFirebaseOfflineError(error)) {
        errorReporter.expected('[Genesis] Failed to mark event completed (client offline)', error, {
          feature: 'firebase',
          operation: 'markCompleted',
        });
      } else {
        errorReporter.critical('[Genesis] Failed to mark event completed', error, {
          feature: 'firebase',
          operation: 'markCompleted',
          userId,
          noteId,
        });
      }
      return false;
    }
  }, [userId, noteId, isForceMode]);

  /**
   * Skip/dismiss the event
   */
  const skipEvent = useCallback(async () => {
    setPhase(GenesisPhase.COMPLETE);
    await markCompleted();
  }, [markCompleted]);

  /**
   * Complete the event (called after all phases finish)
   */
  const completeEvent = useCallback(async () => {
    setPhase(GenesisPhase.COMPLETE);
    await markCompleted();
  }, [markCompleted]);

  return {
    // Eligibility
    eligibility,

    // Event state
    phase,
    isActive: phase !== GenesisPhase.IDLE && phase !== GenesisPhase.COMPLETE,

    // Controls
    startEvent,
    skipEvent,
    completeEvent,

    // Debug/testing
    isForceMode,
  };
};

/**
 * Hook to check if a note is the user's first note
 * Used for permanent visual treatment
 */
export const useIsFirstNote = (noteId?: string) => {
  const { userData } = useAuthStore();
  const userId = userData?.id;
  const [isFirstNote, setIsFirstNote] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !noteId) {
      setIsFirstNote(false);
      setLoading(false);
      return undefined;
    }

    const docRef = doc(collection(db, 'users'), userId);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setIsFirstNote(data?.firstNoteId === noteId);
        } else {
          setIsFirstNote(false);
        }
        setLoading(false);
      },
      (error) => {
        errorReporter.expected('[Genesis] Failed to check first note status', error);
        setIsFirstNote(false);
        setLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [userId, noteId]);

  return { isFirstNote, loading };
};
